import type { TrackletBboxSample, TrackSighting } from "@sitescene/types";

import { useEffect, useRef } from "react";

import { type Rect, videoRect } from "@/features/vision-lab/video-rect";

const BOX_COLOR = "#22c55e";
/** Seconds of the clip shown before the person appears. */
const LEAD_IN_SECONDS = 1;
/** How long past the last sample the box stays: samples are a second apart. */
const HOLD_SECONDS = 1.5;

/**
 * One sighting's clip, starting just before the person appears, with their
 * box drawn over the video. The tracklet keeps a box about once a second,
 * so the box slides between those samples.
 */
export const SightingPlayer = ({ sighting }: { sighting: TrackSighting }) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	// The latest samples, read by the draw loop without restarting it
	const samples = useRef(sighting.bboxSamples);
	useEffect(() => {
		samples.current = sighting.bboxSamples;
	});

	useEffect(() => {
		let frame = 0;
		const draw = () => {
			frame = requestAnimationFrame(draw);
			const canvas = canvasRef.current;
			const video = videoRef.current;
			if (!canvas || !video) return;
			const context = canvas.getContext("2d");
			if (!context) return;

			const ratio = window.devicePixelRatio || 1;
			const width = canvas.clientWidth;
			const height = canvas.clientHeight;
			if (
				canvas.width !== Math.round(width * ratio) ||
				canvas.height !== Math.round(height * ratio)
			) {
				canvas.width = Math.round(width * ratio);
				canvas.height = Math.round(height * ratio);
			}
			context.setTransform(ratio, 0, 0, ratio, 0, 0);
			context.clearRect(0, 0, width, height);

			const view = videoRect(video, width, height);
			const box = boxAt(samples.current, video.currentTime);
			if (view && box) drawBox(context, view, box);
		};
		frame = requestAnimationFrame(draw);
		return () => cancelAnimationFrame(frame);
	}, []);

	const first = sighting.bboxSamples[0];

	return (
		<div className="relative overflow-hidden rounded bg-gray-900">
			<video
				className="aspect-video w-full object-contain"
				controls
				key={sighting.tracklet}
				muted
				onLoadedMetadata={(event) => {
					if (first) {
						event.currentTarget.currentTime = Math.max(
							0,
							first.t - LEAD_IN_SECONDS,
						);
					}
				}}
				playsInline
				preload="metadata"
				ref={videoRef}
				src={sighting.clipUrl}
			/>
			<canvas
				className="pointer-events-none absolute inset-0 h-full w-full"
				ref={canvasRef}
			/>
		</div>
	);
};

/** The person's box at `seconds` into the clip, between the nearest samples. */
const boxAt = (
	samples: TrackletBboxSample[],
	seconds: number,
): null | TrackletBboxSample["bbox"] => {
	const first = samples[0];
	const last = samples[samples.length - 1];
	if (!first || !last) return null;
	if (seconds < first.t - 0.25 || seconds > last.t + HOLD_SECONDS) return null;
	if (seconds <= first.t) return first.bbox;
	if (seconds >= last.t) return last.bbox;

	const index = samples.findIndex((sample) => sample.t > seconds);
	const after = samples[index];
	const before = samples[index - 1];
	if (!after || !before) return last.bbox;
	const blend = (seconds - before.t) / (after.t - before.t || 1);
	return before.bbox.map(
		(value, i) => value + ((after.bbox[i] as number) - value) * blend,
	) as TrackletBboxSample["bbox"];
};

const drawBox = (
	context: CanvasRenderingContext2D,
	view: Rect,
	[x1, y1, x2, y2]: TrackletBboxSample["bbox"],
) => {
	const left = view.x + x1 * view.width;
	const top = view.y + y1 * view.height;
	context.strokeStyle = BOX_COLOR;
	context.lineWidth = 3;
	context.setLineDash([]);
	context.strokeRect(
		left,
		top,
		(x2 - x1) * view.width,
		(y2 - y1) * view.height,
	);
};
